'use client';

import { useState } from 'react';

import { PageSharingProps } from './types';

import IconCheck from '@/assets/icons/iconCheck.svg';
import IconLink from '@/assets/icons/iconShare.svg';
import IconTelegram from '@/assets/icons/iconTelegramCircle.svg';
import IconVk from '@/assets/icons/iconVk.svg';
import IconWhatsapp from '@/assets/icons/iconWhatsapp.svg';
import { classNames } from '@/shared/lib/classNames/classNames';

export const PageSharing: React.FC<PageSharingProps> = ({
  shareText,
  url,
  title,
  copiedText,
  className,
  hideWhatsapp,
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const createLinkClickHandler =
    (type: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      let urlForOpen = '';

      switch (type) {
        case 'vk':
          urlForOpen = `https://vk.com/share.php?url=${url}&title=${title}&utm_source=share2`;
          break;
        case 'telegram':
          urlForOpen = `https://t.me/share/url?url=${url}&text=${title}&utm_source=share2`;
          break;
        case 'whatsapp':
          urlForOpen = `https://api.whatsapp.com/send?text=${title} ${url}&utm_source=share2`;
          break;
        case 'copy':
          navigator.clipboard.writeText(url);

          setIsCopied(true);

          setTimeout(() => {
            setIsCopied(false);
          }, 2000);

          return;
      }

      window.open(urlForOpen, '', 'width=500, height=500');
    };

  return (
    <div
      className={classNames({
        cls: 'relative flex h-[24px] items-center gap-[16px]',
        additional: [className],
      })}
    >
      {!!shareText && (
        <span className="text-text-base-tertiary mr-[8px] text-[16px] leading-[24px] font-normal">
          {shareText}
        </span>
      )}

      <a
        onClick={createLinkClickHandler('telegram')}
        className="text-text-base-tertiary cursor-pointer transition-opacity duration-300 hover:opacity-80 max-lg:order-2"
        aria-label="telegram"
      >
        <IconTelegram className="h-6 w-[25px]" />
      </a>

      <a
        onClick={createLinkClickHandler('vk')}
        className="text-text-base-tertiary cursor-pointer transition-opacity duration-300 hover:opacity-80 max-lg:order-3"
        aria-label="vk"
      >
        <IconVk />
      </a>

      {!hideWhatsapp && (
        <a
          aria-label="whatsapp"
          onClick={createLinkClickHandler('whatsapp')}
          className="text-text-base-tertiary cursor-pointer transition-opacity duration-300 hover:opacity-80 max-lg:order-4"
        >
          <IconWhatsapp className="h-6 w-[25px]" />
        </a>
      )}

      <a
        aria-label="copy"
        onClick={createLinkClickHandler('copy')}
        className="text-text-base-tertiary [&_path]:text-surface-base-primary cursor-pointer transition-opacity duration-300 hover:opacity-80 max-lg:order-1"
      >
        <IconLink />
      </a>

      {isCopied && !!copiedText && (
        <div className="bg-surface-base-secondary text-text-base-tertiary flex h-[32px] items-center rounded-[8px] pr-[8px] pl-[16px] text-[16px] font-normal max-lg:absolute max-lg:top-[30px] max-lg:left-[50px]">
          {copiedText}

          <IconCheck />
        </div>
      )}
    </div>
  );
};
